import { LocalDate } from "@js-joda/core";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api } from "services/api/api.service";
import type { BackendAxiosError } from "services/api/backendError";
import type {
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
  StaffProfileData,
} from "services/api/company/staff.service";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";
import type {
  EditingTab,
  EmploymentSaveMode,
  EmploymentDetailsForm,
  PersonalDetailsForm,
  ProfilePanelTab,
} from "./profilePanel.contract";
import { toEmploymentOptions } from "./domains/employment/profilePanel.options.logic";
import { resolveStaffProfilePanelErrorMessage } from "./domains/employment/profilePanel.error.logic";
import { toLocalDateOrNull } from "./domains/employment/profilePanel.payload.logic";
import {
  toEmploymentUiRules,
  withNormalizedEmploymentFormByRules,
} from "./domains/employment/profilePanel.rules.logic";
import { fetchEmployeeNumberForProfilePanel } from "./domains/employment/profilePanel.employeeNumber.logic";
import { useEmploymentTemplateSelection } from "./domains/employment/useEmploymentTemplateSelection";
import {
  toIsoDateStartOfDayUtc,
  toNonNegativeNumber,
} from "./domains/employment/profilePanel.payload.logic";
import {
  EMPTY_EMPLOYMENT_DETAILS,
  EMPTY_PERSONAL_DETAILS,
  toEmployeeNumberForNewEmployment,
  toEmploymentDetailsForm,
  toPersonalDetailsForm,
} from "./profilePanel.state.formMappers.logic";
import { performSaveEmployment as runPerformSaveEmployment } from "./profilePanel.state.employment.logic";
import { useExecuteSavingAction } from "./profilePanel.state.saveAction.logic";
import {
  canSaveEmploymentEdit as toCanSaveEmploymentEdit,
  isValidPhoneNumber,
} from "./profilePanel.state.validation.logic";
import type {
  AddStaffWorkerTaxRequest,
  UpdateStaffWorkerSalaryRequest,
} from "services/api/company/staff.service";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface CompanySettingValue {
  key: string;
  value?: string | null;
}

interface UseStaffProfilePanelStateParams {
  isOpen: boolean;
  workerId: number | null;
  companyId: number | null;
  initialTab?: ProfilePanelTab;
  profile: StaffProfileData | null;
  unitTeamOptions: UnitTeamOption[];
  subscription: string | undefined;
  companySettings: CompanySettingValue[];
  t: TranslateFn;
  reloadProfile: () => void;
  onWorkerUpdated?: () => void;
}

interface UseStaffProfilePanelStateResult {
  activeTab: ProfilePanelTab;
  editingTab: EditingTab;
  employmentSaveMode: EmploymentSaveMode;
  editedPersonalDetails: PersonalDetailsForm;
  editedEmploymentDetails: EmploymentDetailsForm;
  isSaving: boolean;
  isLoadingEmployeeNumber: boolean;
  employmentTemplates: EmploymentTemplateListItem[];
  isLoadingEmploymentTemplates: boolean;
  selectedEmploymentTemplateId: string;
  applyEmploymentTemplate: (templateId: string) => void;
  templateSalaries: EmploymentTemplateSalary[];
  selectedTemplateSalaryId: string;
  applyEmploymentTemplateSalary: (salaryId: string) => void;
  isSalaryModalOpen: boolean;
  isTaxModalOpen: boolean;
  isEndEmploymentModalOpen: boolean;
  isEndEmploymentForCreateModalOpen: boolean;
  salaryDraftHistory: DraftSalaryItem[];
  taxDraftHistory: DraftTaxItem[];
  effectiveFromDate: LocalDate | null;
  endEmploymentDate: LocalDate | null;
  salaryEarliestStartDate: LocalDate | null;
  saveError: string;
  showLockedEditHint: boolean;
  employmentRules: ReturnType<typeof toEmploymentUiRules>;
  employmentOptions: ReturnType<typeof toEmploymentOptions>;
  teamOptions: SearchableDropdownOption[];
  isProfilePhoneNumberValid: boolean;
  canSaveEmploymentEdit: boolean;
  isDiscardConfirmOpen: boolean;
  setShowLockedEditHint: (value: boolean) => void;
  handleConfirmDiscardClose: () => void;
  handleCancelDiscardClose: () => void;
  handleTabChange: (value: string) => void;
  handleEditClick: () => void;
  handleAddEmploymentClick: () => void;
  handleCancelEdit: () => void;
  handleOpenSaveModal: () => void;
  handleOpenSalaryModal: () => void;
  handleCloseSalaryModal: () => void;
  handleConfirmSalaryModal: () => void;
  handleAddSalaryModal: () => void;
  handleDeleteSalaryModal: (salaryId: number) => void;
  handleOpenTaxModal: () => void;
  handleCloseTaxModal: () => void;
  handleConfirmTaxModal: () => void;
  handleAddTaxModal: () => void;
  handleDeleteTaxModal: (taxId: number) => void;
  handleOpenEndEmploymentModal: () => void;
  handleCloseEndEmploymentModal: () => void;
  handleConfirmEndEmploymentModal: () => void;
  handleCloseEndEmploymentForCreateModal: () => void;
  handleConfirmEndEmploymentForCreateModal: () => void;
  fetchEmployeeNumber: () => void;
  updatePersonalField: <TField extends keyof PersonalDetailsForm>(
    field: TField,
    value: PersonalDetailsForm[TField],
  ) => void;
  updateEmploymentField: <TField extends keyof EmploymentDetailsForm>(
    field: TField,
    value: EmploymentDetailsForm[TField],
  ) => void;
  setEffectiveFromDate: (date: LocalDate | null) => void;
  setEndEmploymentDate: (date: LocalDate | null) => void;
}

type ModalState =
  | { type: "none" }
  | { type: "editSalary" }
  | { type: "editTax" }
  | { type: "endEmployment" }
  | { type: "endEmploymentForCreate" };
type DiscardChangesTarget = "salary" | "tax" | null;

interface DraftSalaryItem {
  id: number;
  startDate: string;
  value: number | null;
}

interface DraftTaxItem {
  id: number;
  startDate: string;
  value: number | null;
}

const toPhoneDigits = (phoneNumber: string): string => {
  return phoneNumber.replace(/\D/g, "").slice(0, 15);
};

const toLatestDate = (first: LocalDate, second: LocalDate): LocalDate => {
  return first.isAfter(second) ? first : second;
};

const toDefaultSalaryChangeDate = (
  nowDate: LocalDate,
  employmentStartDate: LocalDate | null,
): LocalDate => {
  const baselineDate =
    employmentStartDate == null
      ? nowDate
      : toLatestDate(nowDate, employmentStartDate);

  if (baselineDate.dayOfMonth() === 1) {
    return baselineDate;
  }

  const nextMonthDate = baselineDate.plusMonths(1);
  return LocalDate.of(nextMonthDate.year(), nextMonthDate.monthValue(), 1);
};

export const useStaffProfilePanelState = (
  params: UseStaffProfilePanelStateParams,
): UseStaffProfilePanelStateResult => {
  const [activeTab, setActiveTab] = useState<ProfilePanelTab>(
    params.initialTab ?? "profile",
  );
  const [editingTab, setEditingTab] = useState<EditingTab>(null);

  const [employmentSaveMode, setEmploymentSaveMode] =
    useState<EmploymentSaveMode>(null);
  const [editedPersonalDetails, setEditedPersonalDetails] =
    useState<PersonalDetailsForm>(EMPTY_PERSONAL_DETAILS);
  const [editedEmploymentDetails, setEditedEmploymentDetails] =
    useState<EmploymentDetailsForm>(EMPTY_EMPLOYMENT_DETAILS);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({ type: "none" });
  const [effectiveFromDate, setEffectiveFromDate] = useState<LocalDate | null>(
    null,
  );
  const [endEmploymentDate, setEndEmploymentDate] = useState<LocalDate | null>(
    null,
  );
  const [saveError, setSaveError] = useState("");
  const [showLockedEditHint, setShowLockedEditHint] = useState(false);
  const [salaryDraftHistory, setSalaryDraftHistory] = useState<
    DraftSalaryItem[]
  >([]);
  const [salaryInitialHistory, setSalaryInitialHistory] = useState<
    DraftSalaryItem[]
  >([]);
  const [taxDraftHistory, setTaxDraftHistory] = useState<DraftTaxItem[]>([]);
  const [taxInitialHistory, setTaxInitialHistory] = useState<DraftTaxItem[]>(
    [],
  );
  const [discardChangesTarget, setDiscardChangesTarget] =
    useState<DiscardChangesTarget>(null);
  const [hasEndedEmploymentForCreateFlow, setHasEndedEmploymentForCreateFlow] =
    useState(false);
  const employeeNumberRequestIdRef = useRef(0);
  const tempDraftIdRef = useRef(-1);

  const employmentTemplateSelection = useEmploymentTemplateSelection({
    companyId: params.companyId,
    isActive:
      editingTab === "employmentSalary" && employmentSaveMode === "create",
    personalIdentityNumber: params.profile?.personalIdentityNumber ?? null,
    setForm: setEditedEmploymentDetails,
  });

  const employmentRules = useMemo(() => {
    return toEmploymentUiRules(
      editedEmploymentDetails.employmentType,
      editedEmploymentDetails.salaryType,
      params.subscription,
      params.companySettings,
    );
  }, [
    editedEmploymentDetails.employmentType,
    editedEmploymentDetails.salaryType,
    params.companySettings,
    params.subscription,
  ]);

  const employmentOptions = useMemo(() => {
    return toEmploymentOptions(
      params.t,
      employmentRules.isSimflexityEmployee,
      employmentRules.canSelectSimflexityPay,
    );
  }, [
    employmentRules.canSelectSimflexityPay,
    employmentRules.isSimflexityEmployee,
    params.t,
  ]);

  const teamOptions = useMemo(() => {
    return params.unitTeamOptions.flatMap(
      (unit): SearchableDropdownOption[] => {
        const unitName = (unit.unitName ?? "").trim();
        return unit.teams.map((team) => {
          const teamName = (team.name ?? "").trim();
          return {
            value: String(team.id),
            label: unitName.length > 0 ? `${unitName} - ${teamName}` : teamName,
          };
        });
      },
    );
  }, [params.unitTeamOptions]);

  const currentEmploymentStartDate = useMemo(() => {
    return toLocalDateOrNull(
      params.profile?.employmentInfo?.current?.startDate,
    );
  }, [params.profile?.employmentInfo]);

  useEffect(() => {
    const resetTab = params.initialTab ?? "profile";
    if (!params.isOpen) {
      setActiveTab(resetTab);
      setEditingTab(null);
      setEmploymentSaveMode(null);
      setEditedPersonalDetails(EMPTY_PERSONAL_DETAILS);
      setEditedEmploymentDetails(EMPTY_EMPLOYMENT_DETAILS);
      setIsLoadingEmployeeNumber(false);
      setSaveError("");
      setShowLockedEditHint(false);
      setSalaryDraftHistory([]);
      setSalaryInitialHistory([]);
      setTaxDraftHistory([]);
      setTaxInitialHistory([]);
      setEffectiveFromDate(null);
      setEndEmploymentDate(null);
      setDiscardChangesTarget(null);
      setHasEndedEmploymentForCreateFlow(false);
      setModalState({ type: "none" });
      return;
    }

    setActiveTab(resetTab);
    setEditingTab(null);
    setEmploymentSaveMode(null);
    setSaveError("");
    setShowLockedEditHint(false);
    setHasEndedEmploymentForCreateFlow(false);
    setEndEmploymentDate(null);
    setModalState({ type: "none" });
  }, [params.isOpen, params.workerId, params.initialTab]);

  useEffect(() => {
    if (!params.isOpen) return;
    if (editingTab === "profile") return;

    setEditedPersonalDetails((previousValue) => ({
      ...previousValue,
      teamIds:
        params.profile?.workGroups.map((workGroup) => String(workGroup.id)) ??
        [],
    }));
  }, [editingTab, params.isOpen, params.profile?.workGroups]);

  useEffect(() => {
    if (editingTab !== "employmentSalary") return;

    const normalizedValues = withNormalizedEmploymentFormByRules(
      {
        salaryType: editedEmploymentDetails.salaryType,
        employeeCategory: editedEmploymentDetails.employeeCategory,
        paymentType: editedEmploymentDetails.paymentType,
      },
      employmentRules,
    );

    if (
      normalizedValues.salaryType === editedEmploymentDetails.salaryType &&
      normalizedValues.employeeCategory ===
        editedEmploymentDetails.employeeCategory &&
      normalizedValues.paymentType === editedEmploymentDetails.paymentType
    ) {
      return;
    }

    setEditedEmploymentDetails((previousValue) => ({
      ...previousValue,
      salaryType: normalizedValues.salaryType,
      employeeCategory: normalizedValues.employeeCategory,
      paymentType: normalizedValues.paymentType,
    }));
  }, [editedEmploymentDetails, editingTab, employmentRules]);

  const updatePersonalField = useCallback(
    <TField extends keyof PersonalDetailsForm>(
      field: TField,
      value: PersonalDetailsForm[TField],
    ): void => {
      const nextValue =
        field === "phoneNumber" && typeof value === "string"
          ? toPhoneDigits(value)
          : value;
      setEditedPersonalDetails((previousValue) => ({
        ...previousValue,
        [field]: nextValue,
      }));
      setSaveError("");
    },
    [],
  );

  const updateEmploymentField = useCallback(
    <TField extends keyof EmploymentDetailsForm>(
      field: TField,
      value: EmploymentDetailsForm[TField],
    ): void => {
      setEditedEmploymentDetails((previousValue) => ({
        ...previousValue,
        [field]: value,
      }));
    },
    [],
  );

  const fetchEmployeeNumber = useCallback((): void => {
    fetchEmployeeNumberForProfilePanel({
      companyId: params.companyId,
      requestIdRef: employeeNumberRequestIdRef,
      setIsLoadingEmployeeNumber,
      setEditedEmploymentDetails,
    });
  }, [params.companyId]);

  const handleTabChange = useCallback((value: string): void => {
    if (
      value === "profile" ||
      value === "employmentSalary" ||
      value === "history"
    ) {
      setSaveError("");
      setActiveTab(value);
    }
  }, []);

  const handleEditClick = useCallback((): void => {
    if (activeTab === "profile") {
      setEditedPersonalDetails(toPersonalDetailsForm(params.profile));
      setEditingTab("profile");
      return;
    }

    if (activeTab === "history") {
      return;
    }

    setEditedEmploymentDetails(toEmploymentDetailsForm(params.profile));
    setEmploymentSaveMode("update");
    setEditingTab("employmentSalary");
  }, [activeTab, params.profile]);

  const handleAddEmploymentClick = useCallback((): void => {
    if (activeTab !== "employmentSalary") return;
    const hasActiveEmployment =
      params.profile?.employments.some((employment) => employment.isActive) ===
      true;
    if (hasActiveEmployment && !hasEndedEmploymentForCreateFlow) {
      const today = getStockholmLocalDateNow();
      const suggestedDate =
        currentEmploymentStartDate != null &&
        today.isBefore(currentEmploymentStartDate)
          ? currentEmploymentStartDate
          : today;
      setEndEmploymentDate(suggestedDate);
      setSaveError("");
      setModalState({ type: "endEmploymentForCreate" });
      return;
    }

    const employmentDetails = toEmploymentDetailsForm(params.profile);
    setEditedEmploymentDetails({
      ...employmentDetails,
      employeeNumber: toEmployeeNumberForNewEmployment(params.profile),
    });
    setEmploymentSaveMode("create");
    setEditingTab("employmentSalary");
    setSaveError("");
  }, [
    activeTab,
    currentEmploymentStartDate,
    hasEndedEmploymentForCreateFlow,
    params.profile,
  ]);

  const handleCancelEdit = useCallback((): void => {
    employeeNumberRequestIdRef.current += 1;
    setIsLoadingEmployeeNumber(false);
    setEditingTab(null);
    setEmploymentSaveMode(null);
    setModalState({ type: "none" });
    setSaveError("");
  }, []);

  const handleOpenSalaryModal = useCallback((): void => {
    const currentEmployment = params.profile?.employmentInfo?.current;
    const nextHistory =
      currentEmployment?.salaries.map((salary) => ({
        id: salary.id,
        startDate: salary.startDate ?? "",
        value: salary.value,
      })) ?? [];
    setEditedEmploymentDetails(toEmploymentDetailsForm(params.profile));
    setSalaryDraftHistory(nextHistory);
    setSalaryInitialHistory(nextHistory);
    setEffectiveFromDate(
      toDefaultSalaryChangeDate(
        getStockholmLocalDateNow(),
        currentEmploymentStartDate,
      ),
    );
    setSaveError("");
    setModalState({ type: "editSalary" });
  }, [currentEmploymentStartDate, params.profile]);

  const handleCloseSalaryModal = useCallback((): void => {
    if (isSaving) return;
    const isDirty =
      JSON.stringify(salaryDraftHistory) !==
      JSON.stringify(salaryInitialHistory);
    if (isDirty) {
      setDiscardChangesTarget("salary");
      return;
    }
    setModalState({ type: "none" });
  }, [isSaving, salaryDraftHistory, salaryInitialHistory]);

  const handleOpenTaxModal = useCallback((): void => {
    const nextHistory =
      params.profile?.taxes.map((tax) => ({
        id: tax.id,
        startDate: tax.startDate ?? "",
        value: tax.value,
      })) ?? [];
    setEditedEmploymentDetails(toEmploymentDetailsForm(params.profile));
    setTaxDraftHistory(nextHistory);
    setTaxInitialHistory(nextHistory);
    setEffectiveFromDate(
      toDefaultSalaryChangeDate(
        getStockholmLocalDateNow(),
        currentEmploymentStartDate,
      ),
    );
    setSaveError("");
    setModalState({ type: "editTax" });
  }, [currentEmploymentStartDate, params.profile]);

  const handleCloseTaxModal = useCallback((): void => {
    if (isSaving) return;
    const isDirty =
      JSON.stringify(taxDraftHistory) !== JSON.stringify(taxInitialHistory);
    if (isDirty) {
      setDiscardChangesTarget("tax");
      return;
    }
    setSaveError("");
    setModalState({ type: "none" });
  }, [isSaving, taxDraftHistory, taxInitialHistory]);

  const handleConfirmDiscardClose = useCallback((): void => {
    if (discardChangesTarget === "salary") {
      setModalState({ type: "none" });
    } else if (discardChangesTarget === "tax") {
      setSaveError("");
      setModalState({ type: "none" });
    }
    setDiscardChangesTarget(null);
  }, [discardChangesTarget]);

  const handleCancelDiscardClose = useCallback((): void => {
    setDiscardChangesTarget(null);
  }, []);

  const executeSavingAction = useExecuteSavingAction({
    resolveErrorMessage: (error: BackendAxiosError): string =>
      resolveStaffProfilePanelErrorMessage(error, params.t),
    setIsSaving,
    setSaveError,
  });

  const confirmEndEmploymentUpdate = useCallback(
    (onSuccess: () => void): void => {
      if (isSaving) return;
      if (endEmploymentDate == null) {
        setSaveError(params.t("Alerts.SomethingWentWrong"));
        return;
      }

      const currentEmployment = params.profile?.employmentInfo?.current;
      const startDate = toLocalDateOrNull(currentEmployment?.startDate);

      runPerformSaveEmployment({
        companyId: params.companyId,
        workerId: params.workerId,
        profile: params.profile,
        editedEmploymentDetails: toEmploymentDetailsForm(params.profile),
        employmentSaveMode: "update",
        editingTab: "employmentSalary",
        employmentRules,
        startDate,
        endDate: endEmploymentDate,
        translate: params.t,
        setIsSaving,
        setSaveError,
        setEditingTab: (): void => undefined,
        setEmploymentSaveMode: (): void => undefined,
        setModalState: (): void => onSuccess(),
        reloadProfile: params.reloadProfile,
        onWorkerUpdated: params.onWorkerUpdated,
      });
    },
    [
      employmentRules,
      endEmploymentDate,
      isSaving,
      params.companyId,
      params.onWorkerUpdated,
      params.profile,
      params.reloadProfile,
      params.t,
      params.workerId,
    ],
  );

  const performSaveEmployment = useCallback(
    (startDate: LocalDate | null, endDate: LocalDate | null): void => {
      runPerformSaveEmployment({
        companyId: params.companyId,
        workerId: params.workerId,
        profile: params.profile,
        editedEmploymentDetails,
        employmentSaveMode,
        editingTab,
        employmentRules,
        startDate,
        endDate,
        translate: params.t,
        setIsSaving,
        setSaveError,
        setEditingTab: (): void => setEditingTab(null),
        setEmploymentSaveMode: (): void => setEmploymentSaveMode(null),
        setModalState: (): void => setModalState({ type: "none" }),
        reloadProfile: params.reloadProfile,
        onWorkerUpdated: params.onWorkerUpdated,
      });
    },
    [
      editedEmploymentDetails,
      editingTab,
      employmentSaveMode,
      employmentRules,
      params.companyId,
      params.onWorkerUpdated,
      params.profile,
      params.reloadProfile,
      params.t,
      params.workerId,
    ],
  );

  const handleOpenSaveModal = useCallback((): void => {
    if (editingTab === "profile") {
      if (params.profile?.isRegistered !== false) return;
      if (params.companyId == null || params.workerId == null) return;
      if (!isValidPhoneNumber(editedPersonalDetails.phoneNumber)) {
        setSaveError(params.t("Staff.AddStaffPanelPhoneNumberInvalid"));
        return;
      }

      setIsSaving(true);
      setSaveError("");

      Api()
        .company.staff.updateWorkerProfile(params.companyId, params.workerId, {
          firstName: editedPersonalDetails.firstName,
          lastName: editedPersonalDetails.lastName,
          phoneNumber: editedPersonalDetails.phoneNumber,
          email: editedPersonalDetails.email,
          personalIdentityNumber: editedPersonalDetails.personalIdentityNumber,
        })
        .then(() => {
          setEditingTab(null);
          setModalState({ type: "none" });
          params.reloadProfile();
          params.onWorkerUpdated?.();
        })
        .catch((error: BackendAxiosError) => {
          setSaveError(resolveStaffProfilePanelErrorMessage(error, params.t));
        })
        .finally(() => {
          setIsSaving(false);
        });
      return;
    }

    if (editingTab !== "employmentSalary") return;

    const currentEmployment = params.profile?.employmentInfo?.current;
    const nextStartDate =
      editedEmploymentDetails.startDate ??
      toLocalDateOrNull(currentEmployment?.startDate);
    const nextEndDate = editedEmploymentDetails.endDate;

    performSaveEmployment(nextStartDate, nextEndDate);
  }, [
    editedPersonalDetails.email,
    editedPersonalDetails.firstName,
    editedPersonalDetails.lastName,
    editedPersonalDetails.personalIdentityNumber,
    editedPersonalDetails.phoneNumber,
    editedEmploymentDetails.endDate,
    editedEmploymentDetails.startDate,
    editingTab,
    params.companyId,
    params.onWorkerUpdated,
    params.profile,
    params.reloadProfile,
    params.t,
    params.workerId,
    performSaveEmployment,
  ]);

  const handleAddSalaryModal = useCallback((): void => {
    if (effectiveFromDate == null) {
      setSaveError(params.t("Alerts.SomethingWentWrong"));
      return;
    }
    const nextItem: DraftSalaryItem = {
      id: tempDraftIdRef.current--,
      startDate: toIsoDateStartOfDayUtc(effectiveFromDate),
      value: toNonNegativeNumber(editedEmploymentDetails.salary),
    };
    setSalaryDraftHistory((previousValue) => [...previousValue, nextItem]);
    setSaveError("");
  }, [editedEmploymentDetails.salary, effectiveFromDate, params.t]);

  const handleDeleteSalaryModal = useCallback((salaryId: number): void => {
    setSalaryDraftHistory((previousValue) =>
      previousValue.filter((salary) => salary.id !== salaryId),
    );
    setSaveError("");
  }, []);

  const handleConfirmSalaryModal = useCallback((): void => {
    if (isSaving) return;
    const companyId = params.companyId;
    const workerId = params.workerId;
    const employmentId = params.profile?.employmentInfo?.current?.id ?? null;
    if (companyId == null || workerId == null || employmentId == null) {
      setSaveError(params.t("Alerts.SomethingWentWrong"));
      return;
    }
    const initialExistingIds = new Set(
      salaryInitialHistory
        .filter((salary) => salary.id > 0)
        .map((salary) => salary.id),
    );
    const draftExistingIds = new Set(
      salaryDraftHistory
        .filter((salary) => salary.id > 0)
        .map((salary) => salary.id),
    );
    const deletedIds = Array.from(initialExistingIds).filter(
      (id) => !draftExistingIds.has(id),
    );
    const addedItems = salaryDraftHistory.filter((salary) => salary.id < 0);
    const hasChanges = deletedIds.length > 0 || addedItems.length > 0;
    if (!hasChanges) {
      setModalState({ type: "none" });
      return;
    }
    executeSavingAction(async () => {
      for (const salaryId of deletedIds) {
        await Api().company.staff.deleteWorkerSalary(
          companyId,
          workerId,
          employmentId,
          salaryId,
        );
      }
      for (const salary of addedItems) {
        const payload: UpdateStaffWorkerSalaryRequest = {
          salaryId: 0,
          startDate: salary.startDate,
          value: salary.value ?? 0,
        };
        await Api().company.staff.addWorkerSalary(
          companyId,
          workerId,
          payload,
          employmentId,
        );
      }
      setModalState({ type: "none" });
      params.reloadProfile();
      params.onWorkerUpdated?.();
    });
  }, [
    executeSavingAction,
    isSaving,
    params.companyId,
    params.onWorkerUpdated,
    params.profile?.employmentInfo?.current?.id,
    params.reloadProfile,
    params.t,
    params.workerId,
    salaryDraftHistory,
    salaryInitialHistory,
  ]);

  const handleAddTaxModal = useCallback((): void => {
    if (effectiveFromDate == null) {
      setSaveError(params.t("Alerts.SomethingWentWrong"));
      return;
    }
    if (
      currentEmploymentStartDate != null &&
      effectiveFromDate.isBefore(currentEmploymentStartDate)
    ) {
      setSaveError(params.t("Staff.ProfilePanelTaxDateBeforeEmploymentStart"));
      return;
    }
    if (effectiveFromDate.dayOfMonth() !== 1) {
      setSaveError(params.t("Staff.ProfilePanelTaxDateMustBeFirstOfMonth"));
      return;
    }
    const nextItem: DraftTaxItem = {
      id: tempDraftIdRef.current--,
      startDate: toIsoDateStartOfDayUtc(effectiveFromDate),
      value: toNonNegativeNumber(editedEmploymentDetails.taxTable),
    };
    setTaxDraftHistory((previousValue) => [...previousValue, nextItem]);
    setSaveError("");
  }, [
    currentEmploymentStartDate,
    editedEmploymentDetails.taxTable,
    effectiveFromDate,
    params.t,
  ]);

  const handleConfirmTaxModal = useCallback((): void => {
    if (isSaving) return;
    const companyId = params.companyId;
    const workerId = params.workerId;
    if (companyId == null || workerId == null) {
      setSaveError(params.t("Alerts.SomethingWentWrong"));
      return;
    }
    const initialExistingIds = new Set(
      taxInitialHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
    );
    const draftExistingIds = new Set(
      taxDraftHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
    );
    const deletedIds = Array.from(initialExistingIds).filter(
      (id) => !draftExistingIds.has(id),
    );
    const addedItems = taxDraftHistory.filter((tax) => tax.id < 0);
    const hasChanges = deletedIds.length > 0 || addedItems.length > 0;
    if (!hasChanges) {
      setSaveError("");
      setModalState({ type: "none" });
      return;
    }
    executeSavingAction(async () => {
      for (const taxId of deletedIds) {
        await Api().company.staff.deleteWorkerTax(companyId, workerId, taxId);
      }
      const activeTax =
        params.profile?.taxes.find((tax) => tax.isActive) ??
        params.profile?.taxes[0] ??
        null;
      for (const tax of addedItems) {
        const payload: AddStaffWorkerTaxRequest = {
          startDate: tax.startDate,
          taxType: activeTax?.type ?? "Undefined",
          value: tax.value ?? 0,
        };
        await Api().company.staff.addWorkerTax(companyId, workerId, payload);
      }
      setSaveError("");
      setModalState({ type: "none" });
      params.reloadProfile();
      params.onWorkerUpdated?.();
    });
  }, [
    executeSavingAction,
    isSaving,
    params.companyId,
    params.onWorkerUpdated,
    params.profile?.taxes,
    params.reloadProfile,
    params.t,
    params.workerId,
    taxDraftHistory,
    taxInitialHistory,
  ]);

  const handleDeleteTaxModal = useCallback((taxId: number): void => {
    setTaxDraftHistory((previousValue) =>
      previousValue.filter((tax) => tax.id !== taxId),
    );
    setSaveError("");
  }, []);

  const handleOpenEndEmploymentModal = useCallback((): void => {
    if (isSaving) return;
    const today = getStockholmLocalDateNow();
    const suggestedDate =
      currentEmploymentStartDate != null &&
      today.isBefore(currentEmploymentStartDate)
        ? currentEmploymentStartDate
        : today;
    setEndEmploymentDate(suggestedDate);
    setSaveError("");
    setModalState({ type: "endEmployment" });
  }, [currentEmploymentStartDate, isSaving]);

  const handleCloseEndEmploymentModal = useCallback((): void => {
    if (isSaving) return;
    setSaveError("");
    setModalState({ type: "none" });
  }, [isSaving]);

  const handleConfirmEndEmploymentModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      setModalState({ type: "none" });
    });
  }, [confirmEndEmploymentUpdate]);

  const handleCloseEndEmploymentForCreateModal = useCallback((): void => {
    if (isSaving) return;
    setSaveError("");
    setModalState({ type: "none" });
  }, [isSaving]);

  const handleConfirmEndEmploymentForCreateModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      setModalState({ type: "none" });
      const employmentDetails = toEmploymentDetailsForm(params.profile);
      setEditedEmploymentDetails({
        ...employmentDetails,
        employeeNumber: toEmployeeNumberForNewEmployment(params.profile),
      });
      setEmploymentSaveMode("create");
      setEditingTab("employmentSalary");
      setSaveError("");
      setHasEndedEmploymentForCreateFlow(true);
    });
  }, [confirmEndEmploymentUpdate, params.profile]);

  const isSalaryModalOpen = modalState.type === "editSalary";
  const isTaxModalOpen = modalState.type === "editTax";
  const isEndEmploymentModalOpen = modalState.type === "endEmployment";
  const isEndEmploymentForCreateModalOpen =
    modalState.type === "endEmploymentForCreate";
  const isProfilePhoneNumberValid = isValidPhoneNumber(
    editedPersonalDetails.phoneNumber,
  );
  const canSaveEmploymentEdit = useMemo((): boolean => {
    return toCanSaveEmploymentEdit({
      editingTab,
      editedEmploymentDetails,
      isEndDateRequiredOnSave: employmentRules.isEndDateRequiredOnSave,
      employmentSaveMode,
    });
  }, [
    editedEmploymentDetails.employeeCategory,
    editedEmploymentDetails.employeeNumber,
    editedEmploymentDetails.employmentType,
    editedEmploymentDetails.endDate,
    editedEmploymentDetails.paymentType,
    editedEmploymentDetails.role,
    editedEmploymentDetails.salary,
    editedEmploymentDetails.salaryType,
    editedEmploymentDetails.startDate,
    editedEmploymentDetails.workPercentage,
    editingTab,
    employmentRules.isEndDateRequiredOnSave,
    employmentSaveMode,
  ]);

  return {
    activeTab,
    editingTab,
    employmentSaveMode,
    editedPersonalDetails,
    editedEmploymentDetails,
    isSaving,
    isLoadingEmployeeNumber,
    ...employmentTemplateSelection,
    isSalaryModalOpen,
    isTaxModalOpen,
    isEndEmploymentModalOpen,
    isEndEmploymentForCreateModalOpen,
    salaryDraftHistory,
    taxDraftHistory,
    effectiveFromDate,
    endEmploymentDate,
    salaryEarliestStartDate: currentEmploymentStartDate,
    saveError,
    showLockedEditHint,
    employmentRules,
    employmentOptions,
    teamOptions,
    isProfilePhoneNumberValid,
    canSaveEmploymentEdit,
    isDiscardConfirmOpen: discardChangesTarget != null,
    setShowLockedEditHint,
    handleConfirmDiscardClose,
    handleCancelDiscardClose,
    handleTabChange,
    handleEditClick,
    handleAddEmploymentClick,
    handleCancelEdit,
    handleOpenSaveModal,
    handleOpenSalaryModal,
    handleCloseSalaryModal,
    handleConfirmSalaryModal,
    handleAddSalaryModal,
    handleDeleteSalaryModal,
    handleOpenTaxModal,
    handleCloseTaxModal,
    handleConfirmTaxModal,
    handleAddTaxModal,
    handleDeleteTaxModal,
    handleOpenEndEmploymentModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
    fetchEmployeeNumber,
    updatePersonalField,
    updateEmploymentField,
    setEffectiveFromDate,
    setEndEmploymentDate,
  };
};
